import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, IMG } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Users, Building2, ShieldCheck, BadgeCheck, XCircle, Clock, Trash2,
  Star, Mail, Phone, Sparkles, ExternalLink, Settings, Check, KeyRound,
  MapPin, Search, Circle, Filter, BookOpen, IndianRupee, Rocket,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import AiBlogsAdminTab from "@/components/AiBlogsAdminTab";
import UserDetailDrawer from "@/components/UserDetailDrawer";
import OnlineNowPanel from "@/components/OnlineNowPanel";
import CashfreePaymentsTab from "@/components/CashfreePaymentsTab";
import SavedSearchAlertsAdminTab from "@/components/SavedSearchAlertsAdminTab";

const TABS = [
  { key: "overview",       label: "Overview",       icon: <Sparkles size={14} /> },
  { key: "properties",     label: "Properties",     icon: <Building2 size={14} /> },
  { key: "users",          label: "Users",          icon: <Users size={14} /> },
  { key: "verifications",  label: "Verifications",  icon: <ShieldCheck size={14} /> },
  { key: "payments",       label: "Payments",       icon: <IndianRupee size={14} /> },
  { key: "newsletter",     label: "Newsletter",     icon: <Mail size={14} /> },
  { key: "ai-blogs",       label: "AI Blogs",       icon: <BookOpen size={14} /> },
  { key: "settings",       label: "Settings",       icon: <Settings size={14} /> },
];

export default function AdminDashboardPage() {
  const { user, ready } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });

  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [verifStatus, setVerifStatus] = useState("pending");
  const [verifItems, setVerifItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectCode, setRejectCode] = useState("blurry");
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [propRejectTarget, setPropRejectTarget] = useState(null);
  const [propRejectCode, setPropRejectCode] = useState("");
  const [listingReasons, setListingReasons] = useState([]);
  const [propStatusFilter, setPropStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  // Users tab filter state
  const [userQuery, setUserQuery] = useState("");
  const [userState, setUserState] = useState("all");
  const [userCity, setUserCity] = useState("all");
  const [userActiveOnly, setUserActiveOnly] = useState(false);
  const [userSort, setUserSort] = useState("recent");
  const [userLocations, setUserLocations] = useState({ cities: [], states: [] });
  // Settings tab state
  const [siteSettings, setSiteSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  // Newsletter tab state
  const [subscribers, setSubscribers] = useState([]);
  const [digest, setDigest] = useState({
    subject: "",
    heading: "",
    intro: "",
    cta_label: "Read the full guide",
    cta_url: "",
    body_html: "",
    test_only_email: "",
  });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  // Detail drawer state
  const [detailUserId, setDetailUserId] = useState(null);
  // PDF export dialog
  const [pdfDialog, setPdfDialog] = useState(false);
  const [pdfFilters, setPdfFilters] = useState({
    verified: "all",
    role: "all",
    city: "",
    date_from: "",
    date_to: "",
    with_phone_only: false,
  });

  useEffect(() => {
    if (!ready || !user || user.role !== "admin") return;
    const fetchStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    fetchStats();
    // Real-time refresh — poll every 15s so admin sees live counts
    const iv = setInterval(fetchStats, 15000);
    return () => clearInterval(iv);
  }, [ready, user]);

  useEffect(() => {
    if (!ready || !user || user.role !== "admin") return;
    setLoading(true);
    const doFetch = () => {
      if (tab === "properties") {
        const p = propStatusFilter === "all" ? {} : { status: propStatusFilter };
        return api.get("/admin/properties", { params: p }).then(({ data }) => setProperties(data));
      } else if (tab === "users") {
        const p = { sort: userSort };
        if (userQuery) p.q = userQuery;
        if (userState !== "all") p.state = userState;
        if (userCity !== "all") p.city = userCity;
        if (userActiveOnly) p.active_within_min = 15;
        return Promise.all([
          api.get("/admin/users", { params: p }).then(({ data }) => setUsers(data)),
          api.get("/admin/user-locations").then(({ data }) => setUserLocations(data)).catch(() => {}),
        ]);
      } else if (tab === "verifications") {
        return api.get(`/admin/verifications/rich?status=${verifStatus}`).then(({ data }) => setVerifItems(data.items || []));
      } else if (tab === "settings") {
        return api.get("/admin/settings").then(({ data }) => setSiteSettings(data)).catch(() => {});
      } else if (tab === "newsletter") {
        return api.get("/admin/newsletter").then(({ data }) => setSubscribers(data)).catch(() => {});
      }
      return Promise.resolve();
    };
    doFetch().finally(() => setLoading(false));
    if (tab === "users" || tab === "properties") {
      const iv = setInterval(doFetch, 30000);
      return () => clearInterval(iv);
    }
  }, [tab, verifStatus, propStatusFilter, userQuery, userState, userCity, userActiveOnly, userSort, ready, user]);

  // Load preset rejection reasons once — placed before any early return so
  // hook order stays stable across renders.
  useEffect(() => {
    if (!ready || !user || user.role !== "admin") return;
    api.get("/verification/rejection-reasons")
      .then(({ data }) => setRejectionReasons(data.reasons || []))
      .catch(() => {});
    api.get("/listing-rejection-reasons")
      .then(({ data }) => {
        const reasons = data.reasons || [];
        setListingReasons(reasons);
        if (reasons[0]) setPropRejectCode(reasons[0].code);
      })
      .catch(() => {});
  }, [ready, user]);

  if (ready && user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (!ready) return <div className="p-20 text-center text-gray-500">Loading…</div>;

  const deleteProperty = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Deleted");
      setProperties((prev) => prev.filter((p) => p.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const approveUser = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${id}`, { action: "approve" });
      toast.success("Verified");
      setVerifItems((prev) => prev.filter((u) => u.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    // Resolve final human-facing message. If admin picked a preset code,
    // use the canonical user_message from the preset; otherwise use their
    // free-text reason. Guarantees consistent wording to customers.
    const preset = rejectionReasons.find((r) => r.code === rejectCode);
    const finalReason =
      rejectCode === "other" || !preset
        ? rejectReason.trim()
        : (preset.user_message || rejectReason.trim());
    if (!finalReason) {
      toast.error("Please provide a reason so the owner knows how to fix it");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${rejectTarget.id}`, {
        action: "reject",
        reason: finalReason,
        reason_code: rejectCode,
      });
      toast.success("Rejected — owner has been notified with instructions");
      setVerifItems((prev) => prev.filter((u) => u.id !== rejectTarget.id));
      setRejectTarget(null); setRejectReason(""); setRejectCode("blurry");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const moderateProperty = async (id, action) => {
    if (action === "reject") {
      const target = properties.find((p) => p.id === id);
      if (listingReasons[0]) setPropRejectCode(listingReasons[0].code);
      setPropRejectTarget(target || { id });
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/properties/${id}/moderate`, { action });
      toast.success("Approved");
      setProperties((prev) => prev.map((p) => p.id === id ? { ...p, moderation_status: "approved" } : p));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitPropReject = async () => {
    if (!propRejectTarget) return;
    setBusy(true);
    try {
      await api.post(`/admin/properties/${propRejectTarget.id}/moderate`, {
        action: "reject",
        reason_code: propRejectCode,
      });
      toast.success("Rejected — owner notified with a fix & resubmit link");
      setProperties((prev) => prev.map((p) => p.id === propRejectTarget.id ? { ...p, moderation_status: "rejected" } : p));
      setPropRejectTarget(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const downloadCustomersPdf = async () => {
    // Legacy no-filter path — now handled via dialog below
    setBusy(true);
    try {
      const resp = await api.get("/admin/customers.pdf", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `tolet-rentals-customers-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Customer PDF downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const downloadCustomersPdfFiltered = async (filters) => {
    setBusy(true);
    try {
      const params = {};
      if (filters.verified && filters.verified !== "all") params.verified = filters.verified;
      if (filters.role && filters.role !== "all") params.role = filters.role;
      if (filters.city) params.city = filters.city;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.with_phone_only) params.with_phone_only = true;

      const resp = await api.get("/admin/customers.pdf", { params, responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const tag = Object.values(params).join("-").replace(/[^a-z0-9\-]/gi, "") || "all";
      a.download = `tolet-customers-${tag}-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
      setPdfDialog(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const changePassword = async () => {
    if (newPw !== confirmPw) { toast.error("Passwords do not match"); return; }
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const roleBadge = (r) => (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
      r === "admin" ? "bg-[#FF5A3D] text-white" : "bg-gray-100 text-gray-600"
    }`}>{r}</span>
  );

  const verifBadge = (s) => {
    const cls = {
      verified: "bg-emerald-100 text-emerald-700",
      pending: "bg-amber-100 text-amber-700",
      rejected: "bg-red-100 text-red-700",
      unverified: "bg-gray-100 text-gray-500",
    }[s] || "bg-gray-100 text-gray-500";
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${cls}`}>{s}</span>;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <p className="label-eyebrow text-gray-500 mb-2">Admin console</p>
      <h1 className="font-display text-4xl sm:text-5xl brand-forest mb-2 flex items-center gap-3">
        Manage To-Let Rentals <ShieldCheck size={28} className="text-emerald-600" />
      </h1>
      <p className="text-sm text-gray-500 mb-8 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live data — auto-refreshing every 15s
      </p>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8 bg-white rounded-full p-1 border border-brand-subtle w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
              tab === t.key ? "bg-brand-forest text-white" : "text-gray-600 hover:text-[#FF5A3D]"
            }`}
          >{t.icon} {t.label}</button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {stats && [
              { label: "Total users", value: stats.total_users, icon: <Users size={18} /> },
              { label: "Total properties", value: stats.total_properties, icon: <Building2 size={18} /> },
              { label: "Pending properties", value: stats.pending_properties || 0, icon: <Clock size={18} className="text-amber-500" />, cta: () => { setPropStatusFilter("pending"); setTab("properties"); } },
              { label: "Pending verifications", value: stats.pending_verifications, icon: <Clock size={18} className="text-amber-500" />, cta: () => setTab("verifications") },
              { label: "Verified owners", value: stats.verified_owners, icon: <BadgeCheck size={18} className="text-emerald-600" /> },
              { label: "Active boosts", value: stats.active_boosts, icon: <Star size={18} className="text-[#FF5A3D]" /> },
              { label: "Paid transactions", value: stats.completed_transactions, icon: <Sparkles size={18} className="brand-terracotta" /> },
            ].map((s) => (
              <div
                key={s.label}
                onClick={s.cta}
                className={`bg-white border border-brand-subtle rounded-2xl p-6 ${s.cta ? "cursor-pointer hover-lift" : ""}`}
                data-testid={`admin-stat-${s.label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center gap-2 mb-3 brand-forest">{s.icon} <span className="label-eyebrow text-gray-500">{s.label}</span></div>
                <p className="font-display text-4xl brand-forest">{s.value}</p>
              </div>
            ))}
          </div>
          <OnlineNowPanel onUserClick={(id) => setDetailUserId(id)} />
        </div>
      )}

      {/* Properties */}
      {tab === "properties" && (
        <>
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { key: "all", label: "All" },
              { key: "pending", label: "Pending" },
              { key: "approved", label: "Approved" },
              { key: "rejected", label: "Rejected" },
            ].map((f) => (
              <button
                key={f.key}
                data-testid={`prop-filter-${f.key}`}
                onClick={() => setPropStatusFilter(f.key)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${
                  propStatusFilter === f.key ? "bg-brand-forest text-white" : "bg-white text-gray-500 border border-brand-subtle hover:text-[#FF5A3D]"
                }`}
              >{f.label}</button>
            ))}
          </div>
          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading…</div>
          ) : (
            <div className="grid gap-3">
              {properties.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-brand-subtle">Nothing here.</div>
              ) : properties.map((p) => (
                <div key={p.id} className="bg-white border border-brand-subtle rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-property-${p.id}`}>
                  <img
                    src={p.image_paths?.[0] ? IMG(p.image_paths[0]) : "https://images.pexels.com/photos/16110999/pexels-photo-16110999.jpeg?w=200"}
                    alt="" className="w-full md:w-24 h-32 md:h-20 object-cover rounded-xl"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-semibold text-gray-900 line-clamp-1">{p.title}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                        p.listing_type === "rent" ? "bg-[#FFEED9] text-[#FF5A3D]" : "bg-[#E0F2FE] text-[#0EA5E9]"
                      }`}>{p.listing_type}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                        p.moderation_status === "approved" ? "bg-emerald-100 text-emerald-700" :
                        p.moderation_status === "pending" ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      }`}>{p.moderation_status}</span>
                      {p.is_featured && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFEED9] text-[#FF5A3D] text-[10px] font-bold uppercase tracking-widest">
                          <Star size={10} className="fill-[#FF5A3D]" /> Featured
                        </span>
                      )}
                      {p.owner_verified && <BadgeCheck size={14} className="text-emerald-600" />}
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-1">{p.locality}, {p.city} • {p.bhk} BHK • ₹{p.price.toLocaleString("en-IN")}{p.listing_type === "rent" ? "/mo" : ""}</p>
                    <p className="text-xs text-gray-400 mt-1">Owner: {p.owner_name}</p>
                    {p.auto_review?.checks?.some((c) => c.status === "fail") && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`admin-review-flags-${p.id}`}>
                        <span className="text-[11px] font-semibold text-amber-700">⚑ Flagged:</span>
                        {p.auto_review.checks.filter((c) => c.status === "fail").map((c) => (
                          <span key={c.name} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                            {c.name}: {c.detail}
                          </span>
                        ))}
                      </div>
                    )}
                    {p.auto_review?.decision === "approved" && (
                      <p className="text-[11px] text-emerald-600 mt-1" data-testid={`admin-auto-approved-${p.id}`}>✓ Auto-approved by AI checks</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.moderation_status === "pending" && (
                      <>
                        <Button
                          data-testid={`admin-approve-prop-${p.id}`}
                          onClick={() => moderateProperty(p.id, "approve")} disabled={busy}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 px-4"
                        ><Check size={14} className="mr-1" /> Approve</Button>
                        <Button
                          data-testid={`admin-reject-prop-${p.id}`}
                          onClick={() => moderateProperty(p.id, "reject")} disabled={busy}
                          variant="outline"
                          className="rounded-full h-10 px-4 border-red-200 text-red-600 hover:bg-red-50"
                        ><XCircle size={14} className="mr-1" /> Reject</Button>
                      </>
                    )}
                    <Link to={`/property/${p.id}`} target="_blank" className="text-sm brand-forest font-semibold hover:underline inline-flex items-center gap-1 px-2">
                      View <ExternalLink size={12} />
                    </Link>
                    <button
                      onClick={() => deleteProperty(p.id, p.title)}
                      data-testid={`admin-delete-${p.id}`}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100"
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Users */}
      {tab === "users" && (
        <div>
          {/* Filter bar */}
          <div className="bg-white border border-brand-subtle rounded-2xl p-4 mb-5 space-y-3" data-testid="admin-user-filters">
            <div className="flex items-center gap-2 mb-2">
              <Filter size={14} className="text-[#7A1E2A]" />
              <span className="label-eyebrow text-gray-600">Filters</span>
              <button
                onClick={() => setPdfDialog(true)}
                disabled={busy}
                data-testid="admin-download-customers-pdf"
                className="ml-auto text-xs bg-[#7A1E2A] text-white rounded-full px-3 py-1.5 hover:bg-[#8B1F2C] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                title="Download customers as PDF (with filters)"
              >
                📄 Download customers PDF
              </button>
              <span
                data-testid="admin-online-count"
                className="text-xs bg-emerald-100 text-emerald-800 rounded-full px-2.5 py-1 inline-flex items-center gap-1.5"
                title="Users active in the last 15 minutes"
              >
                <Circle size={8} className="fill-emerald-600 text-emerald-600" />
                {users.filter((u) => u.online_now).length} online now
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="col-span-2 md:col-span-2 flex items-center gap-2 border border-brand-subtle rounded-full px-3 h-10 bg-white">
                <Search size={14} className="text-gray-400" />
                <Input
                  data-testid="admin-user-search"
                  placeholder="Name, email or phone"
                  value={userQuery}
                  onChange={(e) => setUserQuery(e.target.value)}
                  className="border-0 h-8 focus-visible:ring-0 bg-transparent px-0"
                />
              </div>
              <Select value={userState} onValueChange={setUserState}>
                <SelectTrigger data-testid="admin-user-state-filter" className="h-10 rounded-full">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All states</SelectItem>
                  {userLocations.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={userCity} onValueChange={setUserCity}>
                <SelectTrigger data-testid="admin-user-city-filter" className="h-10 rounded-full">
                  <SelectValue placeholder="City" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cities</SelectItem>
                  {userLocations.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={userSort} onValueChange={setUserSort}>
                <SelectTrigger data-testid="admin-user-sort" className="h-10 rounded-full">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Newest signups</SelectItem>
                  <SelectItem value="last_login">Latest login</SelectItem>
                  <SelectItem value="logins_desc">Most active</SelectItem>
                  <SelectItem value="name">Name A→Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch
                data-testid="admin-user-active-toggle"
                checked={userActiveOnly}
                onCheckedChange={setUserActiveOnly}
              />
              <span className="text-sm text-gray-700">Show only users online now (last 15 min)</span>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading…</div>
          ) : (
            <div className="bg-white border border-brand-subtle rounded-2xl overflow-hidden">
              <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-brand-subtle label-eyebrow text-gray-500 bg-[#FBF6EC]">
                <div className="col-span-3">User</div>
                <div className="col-span-2">Phone</div>
                <div className="col-span-2">Location</div>
                <div className="col-span-1">Role</div>
                <div className="col-span-1">Verified</div>
                <div className="col-span-2">Last login</div>
                <div className="col-span-1 text-right">Credits</div>
              </div>
              {users.length === 0 && <p className="p-6 text-gray-500 text-center">No matching users.</p>}
              {users.map((u) => (
                <div
                  key={u.id}
                  onClick={() => setDetailUserId(u.id)}
                  className="grid grid-cols-12 gap-3 px-5 py-4 border-t border-brand-subtle items-center text-sm cursor-pointer hover:bg-[#FBF6EC] transition-colors"
                  data-testid={`admin-user-${u.id}`}
                >
                  <div className="col-span-3 min-w-0">
                    <p className="font-semibold text-gray-900 truncate flex items-center gap-2">
                      {u.online_now && (
                        <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]" title="Online now" />
                      )}
                      {u.name || "—"}
                    </p>
                    <p className="text-xs text-gray-500 truncate flex items-center gap-1"><Mail size={11} /> {u.email}</p>
                    {u.login_count > 0 && <p className="text-[10px] text-gray-400 mt-0.5">{u.login_count} logins · {u.revealed_contacts_count} reveals · {u.favorites_count} saved</p>}
                  </div>
                  <div className="col-span-2 text-gray-600 flex items-center gap-1 text-xs">
                    {u.phone ? <><Phone size={12} /> {u.phone}</> : <span className="text-gray-400">—</span>}
                  </div>
                  <div className="col-span-2 text-xs text-gray-600 flex items-center gap-1 min-w-0">
                    {(u.city || u.state) ? (
                      <><MapPin size={11} className="shrink-0" /> <span className="truncate">{[u.city, u.state].filter(Boolean).join(", ")}</span></>
                    ) : <span className="text-gray-400">—</span>}
                  </div>
                  <div className="col-span-1">{roleBadge(u.role)}</div>
                  <div className="col-span-1">{verifBadge(u.verification_status)}</div>
                  <div className="col-span-2 text-xs text-gray-500">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : <span className="text-gray-400">Never</span>}
                  </div>
                  <div className="col-span-1 text-right font-semibold brand-forest">{u.contacts_remaining}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Verifications */}
      {tab === "verifications" && (
        <>
          <div className="flex gap-2 mb-6">
            {["pending", "verified", "rejected"].map((s) => (
              <button
                key={s}
                data-testid={`verif-filter-${s}`}
                onClick={() => setVerifStatus(s)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${
                  verifStatus === s ? "bg-brand-forest text-white" : "bg-white text-gray-500 border border-brand-subtle hover:text-[#FF5A3D]"
                }`}
              >{s}</button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading…</div>
          ) : verifItems.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-brand-subtle">Nothing here.</div>
          ) : (
            <div className="grid gap-3">
              {verifItems.map((u) => (
                <div key={u.id} className="bg-white border border-brand-subtle rounded-2xl p-4 flex flex-col md:flex-row md:items-start gap-4" data-testid={`admin-verif-${u.id}`}>
                  {u.verification_doc_path ? (
                    <a href={IMG(u.verification_doc_path)} target="_blank" rel="noreferrer" className="shrink-0" onClick={(e) => e.stopPropagation()}>
                      <img src={IMG(u.verification_doc_path)} alt="ID" className="w-full md:w-40 h-28 object-cover rounded-xl border border-brand-subtle" />
                    </a>
                  ) : (
                    <div className="w-full md:w-40 h-28 rounded-xl bg-[#FFEED9] flex items-center justify-center text-xs text-gray-500">No doc</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                      <button
                        onClick={() => setDetailUserId(u.id)}
                        className="font-semibold text-lg hover:underline text-left"
                        data-testid={`admin-verif-open-${u.id}`}
                      >{u.name}</button>
                      {u.signup_age_days != null && (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                          Signed up {u.signup_age_days === 0 ? "today" : `${u.signup_age_days}d ago`}
                        </span>
                      )}
                      {u.login_count > 0 && (
                        <span className="text-[10px] text-gray-400">· {u.login_count} logins</span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                      <p className="flex items-center gap-1 truncate"><Mail size={11} className="shrink-0" /> {u.email}</p>
                      {u.phone && <p className="flex items-center gap-1"><Phone size={11} className="shrink-0" /> {u.phone}</p>}
                      {(u.declared_city || u.declared_state) && (
                        <p className="flex items-center gap-1 truncate"><MapPin size={11} className="shrink-0" /> {[u.declared_city, u.declared_state].filter(Boolean).join(", ")}</p>
                      )}
                      {u.ip_location && (
                        <p className="flex items-center gap-1 truncate text-gray-500"><span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" /> IP: {u.ip_location}</p>
                      )}
                      {(u.device || u.browser) && (
                        <p className="flex items-center gap-1 col-span-2 text-gray-500">
                          {u.device === "Mobile" ? "📱" : u.device === "Tablet" ? "📱" : "💻"} {u.device} · {u.browser || "—"} · {u.os || "—"}
                        </p>
                      )}
                      {u.properties_total > 0 && (
                        <p className="flex items-center gap-1 col-span-2 text-gray-500">
                          <Building2 size={11} className="shrink-0" /> {u.properties_total} listings ({u.properties_approved} live)
                        </p>
                      )}
                    </div>
                  </div>
                  {verifStatus === "pending" && (
                    <div className="flex md:flex-col items-center gap-2 shrink-0">
                      <Button
                        data-testid={`admin-approve-${u.id}`}
                        onClick={() => approveUser(u.id)} disabled={busy}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 px-4 w-full"
                      ><BadgeCheck size={14} className="mr-1" /> Approve</Button>
                      <Button
                        data-testid={`admin-reject-${u.id}`}
                        onClick={() => setRejectTarget(u)} variant="outline"
                        className="rounded-full h-10 px-4 border-red-200 text-red-600 hover:bg-red-50 w-full"
                      ><XCircle size={14} className="mr-1" /> Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
            <DialogContent className="max-w-lg rounded-2xl" data-testid="reject-verification-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl brand-forest">Reject verification</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-gray-500 -mt-2">
                Pick the closest reason — the owner will see the standardised instructions and can re-upload.
              </p>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {rejectionReasons.map((r) => (
                  <label
                    key={r.code}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      rejectCode === r.code
                        ? "border-[#7A1E2A] bg-[#FFF3F0]"
                        : "border-brand-subtle hover:border-[#7A1E2A]"
                    }`}
                    data-testid={`reject-reason-${r.code}`}
                  >
                    <input
                      type="radio"
                      name="reject-reason"
                      value={r.code}
                      checked={rejectCode === r.code}
                      onChange={() => setRejectCode(r.code)}
                      className="mt-1 accent-[#7A1E2A]"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{r.label}</p>
                      {r.user_message && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{r.user_message}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              {rejectCode === "other" && (
                <div className="mt-2">
                  <label className="label-eyebrow text-gray-500 text-[10px] block mb-1">
                    Write reason for the owner
                  </label>
                  <Input
                    data-testid="reject-reason-other-text"
                    autoFocus
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Explain what went wrong so they can fix it"
                    className="h-10"
                  />
                </div>
              )}
              <DialogFooter className="gap-2 mt-2">
                <Button variant="ghost" onClick={() => setRejectTarget(null)} className="rounded-full">Cancel</Button>
                <Button
                  onClick={submitReject}
                  disabled={busy}
                  data-testid="reject-verification-confirm"
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full"
                >Reject &amp; notify owner</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
      {/* Newsletter */}
      {tab === "newsletter" && (
        <div className="grid md:grid-cols-5 gap-6">
          {/* Compose */}
          <div className="md:col-span-3 bg-white border border-brand-subtle rounded-2xl p-6" data-testid="admin-newsletter-compose">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#FBF6EC] flex items-center justify-center text-[#7A1E2A]"><Mail size={20} /></div>
              <div>
                <p className="label-eyebrow text-gray-500">Newsletter</p>
                <h2 className="font-display text-2xl brand-forest">Send weekly digest</h2>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Every field below becomes part of one royal maroon-and-gold email. Test with your own address first, then send to all {subscribers.length} subscribers.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold mb-1 block">Subject line</label>
                <Input data-testid="digest-subject" value={digest.subject} onChange={(e) => setDigest({ ...digest, subject: e.target.value })} placeholder="This week on To-Let Rentals: Renting in Bengaluru 2026" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Main heading</label>
                <Input data-testid="digest-heading" value={digest.heading} onChange={(e) => setDigest({ ...digest, heading: e.target.value })} placeholder="Renting in Bengaluru? Read this first." />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Intro paragraph</label>
                <textarea
                  data-testid="digest-intro"
                  rows={3}
                  className="w-full rounded-md border border-brand-subtle px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#7A1E2A]/30"
                  value={digest.intro}
                  onChange={(e) => setDigest({ ...digest, intro: e.target.value })}
                  placeholder="Every year, 350,000+ people move to Bengaluru. This 9-minute guide covers exact rent ranges…"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="text-xs font-semibold mb-1 block">Button label</label>
                  <Input data-testid="digest-cta-label" value={digest.cta_label} onChange={(e) => setDigest({ ...digest, cta_label: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold mb-1 block">Button link (URL)</label>
                  <Input data-testid="digest-cta-url" value={digest.cta_url} onChange={(e) => setDigest({ ...digest, cta_url: e.target.value })} placeholder="https://to-let-rentals.com/blog/…" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Test send only to (leave blank for full send)</label>
                <Input data-testid="digest-test-email" value={digest.test_only_email} onChange={(e) => setDigest({ ...digest, test_only_email: e.target.value })} placeholder="you@yourdomain.com — get a preview first" />
                <p className="text-[11px] text-gray-500 mt-1">
                  While using the Resend sandbox address, sends only go through to your Resend-account email. Verify a domain at
                  {" "}<a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#7A1E2A] underline">resend.com/domains</a>{" "}
                  to send to all subscribers.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  data-testid="digest-send-test"
                  disabled={sending || !digest.subject || !digest.heading || !digest.cta_url || !digest.test_only_email}
                  onClick={async () => {
                    setSending(true); setSendResult(null);
                    try {
                      const { data } = await api.post("/admin/newsletter/send", { ...digest });
                      setSendResult(data);
                      toast.success(`Test sent — ${data.sent}/${data.total} delivered`);
                    } catch (err) { toast.error(formatApiError(err)); }
                    finally { setSending(false); }
                  }}
                  className="rounded-full h-11 px-5 bg-[#7A1E2A]/10 text-[#7A1E2A] border border-[#7A1E2A]/25 hover:bg-[#7A1E2A]/20"
                >
                  Send test to me
                </Button>
                <Button
                  data-testid="digest-send-all"
                  disabled={sending || !digest.subject || !digest.heading || !digest.cta_url || subscribers.length === 0}
                  onClick={async () => {
                    if (!window.confirm(`Send this digest to all ${subscribers.length} subscribers? This action cannot be undone.`)) return;
                    setSending(true); setSendResult(null);
                    try {
                      const { data } = await api.post("/admin/newsletter/send", { ...digest, test_only_email: null });
                      setSendResult(data);
                      toast.success(`Sent — ${data.sent}/${data.total} delivered${data.failed ? `, ${data.failed} failed` : ""}`);
                    } catch (err) { toast.error(formatApiError(err)); }
                    finally { setSending(false); }
                  }}
                  className="tlr-gold-btn rounded-full h-11 px-6"
                >
                  {sending ? "Sending…" : `Send to all ${subscribers.length} subscribers`}
                </Button>
              </div>
              {sendResult && (
                <div className="mt-3 text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-3">
                  Delivered {sendResult.sent} of {sendResult.total}. {sendResult.failed > 0 && <span className="text-red-700">{sendResult.failed} failed (see server logs).</span>}
                </div>
              )}
            </div>
          </div>

          {/* Subscriber list */}
          <div className="md:col-span-2 bg-white border border-brand-subtle rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl brand-forest">Subscribers</h3>
              <span className="text-xs bg-[#FBF6EC] text-[#7A1E2A] rounded-full px-3 py-1 font-semibold">{subscribers.length} total</span>
            </div>
            {subscribers.length === 0 ? (
              <p className="text-sm text-gray-500">No signups yet. Once readers subscribe on your blog, they'll appear here.</p>
            ) : (
              <div className="max-h-[540px] overflow-y-auto -mx-2">
                {subscribers.map((s) => (
                  <div key={s.id} className="px-3 py-2.5 border-b border-brand-subtle last:border-b-0 text-sm">
                    <p className="font-semibold text-gray-900 truncate">{s.email}</p>
                    <p className="text-[11px] text-gray-500 flex items-center gap-2">
                      <span>{s.source || "direct"}</span>
                      <span>·</span>
                      <span>{s.created_at ? new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}</span>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Saved search alerts */}
      {tab === "newsletter" && <div className="mt-6"><SavedSearchAlertsAdminTab /></div>}

      {/* AI Blogs */}
      {tab === "ai-blogs" && <AiBlogsAdminTab />}

      {/* Cashfree payments (read-only) */}
      {tab === "payments" && <CashfreePaymentsTab kind="all" />}

      {/* Settings */}
      {tab === "settings" && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Site settings */}
          <div className="bg-white border border-brand-subtle rounded-2xl p-8" data-testid="admin-site-settings">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#FBF6EC] flex items-center justify-center text-[#7A1E2A]">
                <Settings size={22} />
              </div>
              <div>
                <p className="label-eyebrow text-gray-500">Site</p>
                <h2 className="font-display text-2xl brand-forest">Global settings</h2>
              </div>
            </div>
            {!siteSettings ? (
              <p className="text-gray-500 text-sm">Loading…</p>
            ) : (
              <div className="space-y-5">
                <label className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Boost / paid promotions</p>
                    <p className="text-xs text-gray-500 mt-0.5">Turn on once Razorpay is live so owners can promote listings.</p>
                  </div>
                  <Switch
                    data-testid="setting-boost-enabled"
                    checked={siteSettings.boost_enabled}
                    onCheckedChange={(v) => setSiteSettings((s) => ({ ...s, boost_enabled: v }))}
                  />
                </label>
                <label className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Open registrations</p>
                    <p className="text-xs text-gray-500 mt-0.5">Turn off to temporarily stop new signups.</p>
                  </div>
                  <Switch
                    data-testid="setting-registration-open"
                    checked={siteSettings.registration_open}
                    onCheckedChange={(v) => setSiteSettings((s) => ({ ...s, registration_open: v }))}
                  />
                </label>
                <div>
                  <label className="text-sm font-semibold mb-1 block">Free contact reveals per user</label>
                  <Input
                    data-testid="setting-free-credits"
                    type="number"
                    inputMode="numeric"
                    value={siteSettings.contact_free_credits}
                    onChange={(e) => setSiteSettings((s) => ({ ...s, contact_free_credits: +e.target.value || 0 }))}
                  />
                  <p className="text-xs text-gray-500 mt-1">How many owner contacts a signed-in user can reveal for free.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-semibold mb-1 block">Support email</label>
                    <Input
                      data-testid="setting-support-email"
                      type="email"
                      value={siteSettings.support_email}
                      onChange={(e) => setSiteSettings((s) => ({ ...s, support_email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold mb-1 block">Support phone</label>
                    <Input
                      data-testid="setting-support-phone"
                      value={siteSettings.support_phone}
                      onChange={(e) => setSiteSettings((s) => ({ ...s, support_phone: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Cashfree payments — active gateway (read-only info) */}
                <div className="pt-4 mt-2 border-t border-brand-subtle">
                  <div className="flex items-start gap-3 bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl p-4">
                    <IndianRupee size={16} className="text-[#0369A1] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-[#0369A1]">Cashfree is your active payment gateway</p>
                      <p className="text-xs text-[#0C4A6E] mt-0.5">
                        Boost and contact-pack payments are collected automatically via Cashfree and unlock instantly on confirmed payment. Keys are configured on the server (<code>CASHFREE_APP_ID</code> / <code>CASHFREE_SECRET_KEY</code>). Currently in <strong>Sandbox/Test</strong> mode. See all transactions in the <strong>Payments</strong> tab.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  data-testid="setting-save"
                  disabled={savingSettings}
                  onClick={async () => {
                    setSavingSettings(true);
                    try {
                      const { data } = await api.put("/admin/settings", siteSettings);
                      setSiteSettings(data);
                      toast.success("Settings saved");
                    } catch (err) { toast.error(formatApiError(err)); }
                    finally { setSavingSettings(false); }
                  }}
                  className="tlr-gold-btn rounded-full h-11 px-6 w-full"
                >
                  {savingSettings ? "Saving…" : "Save settings"}
                </Button>
              </div>
            )}
          </div>

          {/* Password change */}
          <div className="bg-white border border-brand-subtle rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#FBF6EC] flex items-center justify-center text-[#7A1E2A]">
                <KeyRound size={22} />
              </div>
              <div>
                <p className="label-eyebrow text-gray-500">Admin</p>
                <h2 className="font-display text-2xl brand-forest">Change password</h2>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">Rotate your admin password anytime. You'll stay logged in on this browser.</p>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-semibold mb-1 block">Current password</label>
                <Input data-testid="pw-current" type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">New password</label>
                <Input data-testid="pw-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Confirm new password</label>
                <Input data-testid="pw-confirm" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
              </div>
              <Button
                data-testid="pw-submit"
                onClick={changePassword} disabled={busy || !currentPw || !newPw || !confirmPw}
                className="tlr-gold-btn rounded-full h-11 px-6 w-full"
              >{busy ? "Updating…" : "Update password"}</Button>
            </div>
          </div>

          {/* Seed sample catalog — one-click populate 75 curated demo properties
              with real photos so a fresh install looks alive from day one.
              Idempotent: existing listings are preserved, only NEW titles insert.
              Especially useful right after deploying to a brand-new production
              database. */}
          <div className="bg-white border border-brand-subtle rounded-2xl p-8" data-testid="admin-seed-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#FBF6EC] flex items-center justify-center text-[#7A1E2A]">
                <Sparkles size={22} />
              </div>
              <div>
                <p className="label-eyebrow text-gray-500">Setup</p>
                <h2 className="font-display text-2xl brand-forest">Seed sample catalog</h2>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Populate this environment with <strong>75 curated properties</strong> across 17 cities and <strong>6 verified owner accounts</strong>. All photos are real Unsplash property shots. Safe to click multiple times — only new titles are added, existing listings stay put.
            </p>
            <Button
              data-testid="seed-catalog-btn"
              onClick={async () => {
                if (!window.confirm("Populate this environment with 75 demo properties? Existing listings will be untouched.")) return;
                try {
                  const { data } = await api.post("/admin/seed-catalog");
                  toast.success(`Seeded ${data.properties_inserted} new properties (${data.properties_skipped} already existed)`);
                  api.get("/admin/stats").then(({ data: s }) => setStats(s)).catch(() => {});
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Seed failed");
                }
              }}
              className="w-full h-11 rounded-full bg-[#7A1E2A] hover:bg-[#5C1420] text-white font-semibold"
            >
              <Sparkles size={15} className="mr-2" /> Populate catalog
            </Button>
            <p className="text-[11px] text-gray-500 mt-3">
              Tip: Run this once on a fresh production database. New visitors trust a site that already shows a lively catalog.
            </p>
          </div>
        </div>
      )}

      {/* User Detail Drawer — mounted globally so it works from Overview (online panel),
          Users tab (row click) and Verifications tab (name click). */}
      <UserDetailDrawer
        userId={detailUserId}
        open={!!detailUserId}
        onClose={() => setDetailUserId(null)}
      />

      {/* Listing reject — one-tap reason picker; owner is emailed a fix & resubmit link */}
      <Dialog open={!!propRejectTarget} onOpenChange={(o) => !o && setPropRejectTarget(null)}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="reject-listing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl brand-forest">Decline this listing</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2">
            Pick the closest reason. The owner gets an email with clear next steps and a one-tap link to fix &amp; resubmit.
          </p>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {listingReasons.map((r) => (
              <label
                key={r.code}
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  propRejectCode === r.code
                    ? "border-[#7A1E2A] bg-[#FFF3F0]"
                    : "border-brand-subtle hover:border-[#7A1E2A]"
                }`}
                data-testid={`listing-reject-reason-${r.code}`}
              >
                <input
                  type="radio"
                  name="listing-reject-reason"
                  value={r.code}
                  checked={propRejectCode === r.code}
                  onChange={() => setPropRejectCode(r.code)}
                  className="mt-1 accent-[#7A1E2A]"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{r.label}</p>
                  {r.user_message && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{r.user_message}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="ghost" onClick={() => setPropRejectTarget(null)} className="rounded-full">Cancel</Button>
            <Button
              onClick={submitPropReject}
              disabled={busy || !propRejectCode}
              data-testid="reject-listing-confirm"
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
            >Decline &amp; notify owner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Customers PDF export — filter dialog */}
      <Dialog open={pdfDialog} onOpenChange={setPdfDialog}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="pdf-export-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl brand-forest">Export customers PDF</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2 mb-3">All filters are optional. Combine any to narrow the list.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow text-gray-500 text-[10px] block mb-1">Verification</label>
              <Select value={pdfFilters.verified} onValueChange={(v) => setPdfFilters({ ...pdfFilters, verified: v })}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="pdf-filter-verified"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="verified">Verified only</SelectItem>
                  <SelectItem value="unverified">Unverified</SelectItem>
                  <SelectItem value="pending">Pending review</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-eyebrow text-gray-500 text-[10px] block mb-1">Role</label>
              <Select value={pdfFilters.role} onValueChange={(v) => setPdfFilters({ ...pdfFilters, role: v })}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="pdf-filter-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="user">Users</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="label-eyebrow text-gray-500 text-[10px] block mb-1">City (contains)</label>
              <Input
                data-testid="pdf-filter-city"
                placeholder="e.g. Bengaluru"
                value={pdfFilters.city}
                onChange={(e) => setPdfFilters({ ...pdfFilters, city: e.target.value })}
                className="h-10 rounded-lg"
              />
            </div>
            <div>
              <label className="label-eyebrow text-gray-500 text-[10px] block mb-1">Signed up from</label>
              <Input
                data-testid="pdf-filter-from"
                type="date"
                value={pdfFilters.date_from}
                onChange={(e) => setPdfFilters({ ...pdfFilters, date_from: e.target.value })}
                className="h-10 rounded-lg"
              />
            </div>
            <div>
              <label className="label-eyebrow text-gray-500 text-[10px] block mb-1">Signed up to</label>
              <Input
                data-testid="pdf-filter-to"
                type="date"
                value={pdfFilters.date_to}
                onChange={(e) => setPdfFilters({ ...pdfFilters, date_to: e.target.value })}
                className="h-10 rounded-lg"
              />
            </div>
            <label className="col-span-2 flex items-center gap-3 mt-1 p-3 border border-brand-subtle rounded-lg cursor-pointer" data-testid="pdf-filter-phone-only-wrap">
              <Switch
                data-testid="pdf-filter-phone-only"
                checked={pdfFilters.with_phone_only}
                onCheckedChange={(v) => setPdfFilters({ ...pdfFilters, with_phone_only: v })}
              />
              <span className="text-sm">Include only customers with a mobile number</span>
            </label>
          </div>
          <DialogFooter className="mt-4 flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setPdfFilters({ verified: "all", role: "all", city: "", date_from: "", date_to: "", with_phone_only: false })}
              className="rounded-full h-10"
              data-testid="pdf-filter-reset"
            >Reset</Button>
            <Button
              onClick={() => downloadCustomersPdfFiltered(pdfFilters)}
              disabled={busy}
              data-testid="pdf-filter-download"
              className="tlr-gold-btn rounded-full h-10 px-5"
            >{busy ? "Preparing…" : "📄 Download PDF"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
